// base
import React, { useMemo } from 'react';

// styles
import { StyledPagination } from './style';

// hooks
import { type Pagination as PaginationHookProps } from 'hooks';

// constants
import { DEFAULT_PAGE_SIZE, STICKY_TABLE_PAGE_VIEW } from 'constants/const';
import { arrowImage } from 'constants/images';

// libraries
import { isBoolean } from 'lodash-es';
import {
  MenuItem,
  Select,
  FormControl,
  type SelectChangeEvent,
  Pagination as MuiPagination
} from '@mui/material';

export interface PaginationProps {
  isAbsoluteToBottom?: boolean;
  pagination?: PaginationHookProps | boolean;
  onChangePageSize?: (page: number, pageSize: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  isAbsoluteToBottom = false,
  pagination: _pagination,
  onChangePageSize
}) => {
  const tablePageSelectView = useMemo(
    () =>
      STICKY_TABLE_PAGE_VIEW.map((item) => ({
        value: item,
        label: String(item)
      })),
    []
  );

  const pagination = useMemo<PaginationHookProps>(() => {
    const defaultConfig: PaginationHookProps = {
      total: 0,
      current: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      showSizeChanger: false,
      onChange: () => {}
    };

    if (!_pagination || isBoolean(_pagination)) {
      return defaultConfig;
    }

    return {
      ...defaultConfig,
      ..._pagination
    };
  }, [_pagination]);

  const paginationCount = useMemo(() => {
    const r = pagination.total / pagination.pageSize;

    if (r === 0) {
      return 1;
    }

    return Math.ceil(r);
  }, [pagination]);

  const handleChangePageSize = (e: SelectChangeEvent<number>) => {
    const isSelected = Number(e.target.value);

    if (!onChangePageSize) return;

    onChangePageSize(0, isSelected);
  };

  const handleChangePage = (newPage: number) => {
    const curresnPage = newPage > 0 ? newPage - 1 : 0;
    const page = curresnPage < 0 ? 0 : curresnPage;

    if (!onChangePageSize) return;

    onChangePageSize(page, pagination.pageSize);
  };

  return (
    <StyledPagination>
      <div className={`pagination ${isAbsoluteToBottom ? 'absolute-bottom' : ''}`}>
        <div className="pagination-select">
          <div className="pagination-select-label">
            <p>Items per page</p>
          </div>
          <div className="pagination-select-option">
            <FormControl>
              <Select
                value={pagination.pageSize}
                onChange={handleChangePageSize}
                IconComponent={() => (
                  <img src={arrowImage.arrowDropDown} alt="select icon" className="arrowImg" />
                )}
              >
                {tablePageSelectView.map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <MuiPagination
          count={paginationCount}
          page={pagination.current + 1}
          onChange={(_, value) => handleChangePage(value)}
          shape="rounded"
        />
      </div>
    </StyledPagination>
  );
};
